import PropTypes from 'prop-types'
import Dinero from 'dinero.js'
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Divider,
  Flex,
  Text,
} from '@chakra-ui/react'
import {useTranslation} from 'react-i18next'
import DineroPropShape from '~/utils/propTypes/dinero'
import {ExpandablePanel} from '../ExpandablePanel'
import {formPropTypes} from '../formPropTypes'
import {PayablePropType} from '../../../../propTypes'
import {calculateFees} from '../../helpers/feeCalculator'

const ReviewItem = ({label, value}) => {
  return (
    <Flex justify="space-between">
      <Text textStyle="body-copy">{label}</Text>
      <Text textStyle="body-copy-medium">{value}</Text>
    </Flex>
  )
}

ReviewItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
}

const FeeBreakdown = ({feeAmount, taxAmount, totalFeeAmount}) => {
  const {t} = useTranslation('pointsOnPayments')

  return (
    <Accordion allowToggle mx="-sm">
      <AccordionItem border="none">
        <AccordionButton>
          <Flex width="100%" justify="space-between">
            <Flex gap="xs">
              <Text textStyle="body-copy">
                {t(
                  'pointsOnPayments:paymentWizard.formPanel.reviewPanel.totalFeeLabel',
                )}
              </Text>
              <AccordionIcon />
            </Flex>
            <Text alignSelf="end" textStyle="body-copy">
              {totalFeeAmount.toFormat()}
            </Text>
          </Flex>
        </AccordionButton>
        <AccordionPanel color="mld.neutral.700" py="0">
          <Flex width="100%" justify="space-between">
            <Text textStyle="body-copy">
              {t(
                'pointsOnPayments:paymentWizard.formPanel.reviewPanel.feeAmountLabel',
              )}
            </Text>
            <Text textStyle="body-copy">{feeAmount.toFormat()}</Text>
          </Flex>
          <Flex width="100%" justify="space-between">
            <Text textStyle="body-copy">
              {t(
                'pointsOnPayments:paymentWizard.formPanel.reviewPanel.taxAmountLabel',
              )}
            </Text>
            <Text textStyle="body-copy">{taxAmount.toFormat()}</Text>
          </Flex>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  )
}

FeeBreakdown.propTypes = {
  feeAmount: PropTypes.shape(DineroPropShape).isRequired,
  taxAmount: PropTypes.shape(DineroPropShape).isRequired,
  totalFeeAmount: PropTypes.shape(DineroPropShape).isRequired,
}

const ReviewPanel = ({payable, values, isValid}) => {
  const {t} = useTranslation('pointsOnPayments')

  const paymentAmount = Dinero({
    amount: parseInt(values.amount, 10),
  })

  const {feeAmount, taxAmount, totalFeeAmount, totalAmount} = calculateFees({
    paymentAmount,
    offer: payable.offer,
  })

  return (
    <ExpandablePanel
      title={t('pointsOnPayments:paymentWizard.formPanel.reviewPanel.title')}
      isDisabled={!isValid}
    >
      <Flex direction="column" gap="md">
        <Flex direction="column" gap="xxs">
          <ReviewItem
            label={t(
              'pointsOnPayments:paymentWizard.formPanel.reviewPanel.payeeLabel',
            )}
            value={payable.payeeName}
          />
          <ReviewItem
            label={t(
              'pointsOnPayments:paymentWizard.formPanel.reviewPanel.branchCodeLabel',
            )}
            value={payable.payee.bankAccount.bsb}
          />
          <ReviewItem
            label={t(
              'pointsOnPayments:paymentWizard.formPanel.reviewPanel.accountNumberLabel',
            )}
            value={payable.payee.bankAccount.accountNumber}
          />
        </Flex>
        <Divider borderColor="mld.neutral.700" />
        <Flex direction="column" gap="xs">
          <ReviewItem
            label={t(
              'pointsOnPayments:paymentWizard.formPanel.reviewPanel.payoutAmountLabel',
            )}
            value={paymentAmount.toFormat()}
          />
          <FeeBreakdown
            feeAmount={feeAmount}
            taxAmount={taxAmount}
            totalFeeAmount={totalFeeAmount}
          />
          <Divider borderColor="mld.neutral.700" />
          <Flex justify="space-between">
            <Text textStyle="body-copy" fontWeight="bold">
              {t(
                'pointsOnPayments:paymentWizard.formPanel.reviewPanel.totalLabel',
              )}
            </Text>
            <Text textStyle="body-copy" fontWeight="bold">
              {totalAmount.toFormat()}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </ExpandablePanel>
  )
}

ReviewPanel.propTypes = {
  payable: PayablePropType,
  values: formPropTypes.isRequired,
  isValid: PropTypes.bool.isRequired,
}

ReviewPanel.defaultProps = {
  payable: null,
}

export default ReviewPanel
